import React, { useState } from 'react';
import { Check, Image as ImageIcon, Loader2, MapPin, Monitor, Smartphone, Wifi, X, UserSquare2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FaceBadge, FlagBadges, StatusBadge } from '@/components/staffAttendance/Badges';
import { fmtTime } from '@/services/staffAttendanceApi';
import { WorkRoleSelect } from '@/components/staffAttendance/WorkRole';

const Photo = ({ url, label, testId }) => (
  <div className="flex-1 min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{label}</div>
    {url ? <img src={url} alt={label} className="w-full aspect-[3/4] object-cover rounded-xl bg-gray-100" data-testid={testId} />
      : <div className="w-full aspect-[3/4] rounded-xl bg-gray-100 flex flex-col items-center justify-center text-gray-400 text-xs" data-testid={`${testId}-none`}><ImageIcon className="w-8 h-8 mb-1" />No reference photo</div>}
  </div>
);

const Place = ({ p }) => (
  <div className="text-xs text-gray-600 space-y-0.5">
    {p.location_name && <div className="flex items-center gap-1" data-testid={`punch-where-${p.punch_id}`}><MapPin className="w-3 h-3" />{p.on_site === false && p.nearest_location ? `${p.location_name} · ${p.distance_m} m from ${p.nearest_location}` : `${p.location_name}${p.distance_m != null ? ` · ${p.distance_m} m` : ''}`}</div>}
    {(p.office || p.network) && <div className="flex items-center gap-1"><Wifi className="w-3 h-3" />{p.office || p.network}{p.ip ? ` · ${p.ip}` : ''}</div>}
    <div className="flex items-center gap-1">{p.device_type === 'kiosk' ? <Monitor className="w-3 h-3" /> : <Smartphone className="w-3 h-3" />}{p.device_type === 'kiosk' ? `Section device${p.kiosk_name ? ` · ${p.kiosk_name}` : ''}` : 'Own phone'}</div>
  </div>
);

/** One punch for the supervisor: selfie next to the reference photo + Approve / Reject. */
export const PunchCard = ({ p, onApprove, onReject, onUseAsRef }) => {
  const [busy, setBusy] = useState(false);
  const run = (fn) => async () => { setBusy(true); try { await fn(p); } finally { setBusy(false); } };
  const isIn = p.kind === 'in';
  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid={`punch-card-${p.punch_id}`}>
      <div className={`h-1.5 ${p.face_result === 'match' ? 'bg-emerald-500' : p.face_result === 'check' ? 'bg-amber-400' : 'bg-gray-300'}`} />
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-bold text-lg leading-tight" data-testid={`punch-name-${p.punch_id}`}>{p.staff_name}</div>
            <div className="text-xs text-gray-500">{p.staff_id} · {p.section}</div>
          </div>
          <div className="text-right space-y-1">
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold ${isIn ? 'bg-emerald-600 text-white' : 'bg-indigo-600 text-white'}`} data-testid={`punch-kind-${p.punch_id}`}>{isIn ? 'Check In' : 'Check Out'}</span>
            <div className="text-sm font-mono">{p.date} · {fmtTime(p.time)}</div>
          </div>
        </div>
        <div className="flex gap-3">
          <Photo url={p.selfie_url} label="Selfie" testId={`punch-selfie-${p.punch_id}`} />
          <Photo url={p.reference_url} label="Reference" testId={`punch-ref-${p.punch_id}`} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FaceBadge result={p.face_result} score={p.face_score} testId={`punch-face-${p.punch_id}`} />
          <StatusBadge status={p.status} testId={`punch-status-${p.punch_id}`} />
          <FlagBadges p={p} testId={`punch-flag-${p.punch_id}`} />
        </div>
        {p.on_site === false && p.status === 'pending' && (
          <div className="rounded-lg bg-slate-800 text-white text-xs px-3 py-2" data-testid={`punch-offsite-note-${p.punch_id}`}>
            Off-site punch{p.distance_m != null ? ` · ${p.distance_m} m from ${p.nearest_location || 'site'}` : ''} — check and decide manually. If the work-location pin is wrong, fix it in Settings → Work locations.
          </div>
        )}
        <Place p={p} />
        {p.kind === 'in' && p.worker_type === 'contract' && p.status !== 'rejected' && <WorkRoleSelect p={p} />}
        {p.status !== 'pending' && (
          <div className="text-xs text-gray-500" data-testid={`punch-decided-${p.punch_id}`}>{p.status === 'approved' ? 'Approved' : 'Rejected'} by {p.decided_by_name} · {fmtTime(p.decided_at)}{p.reason ? ` — ${p.reason}` : ''}</div>
        )}
        {p.status === 'pending' && (
          <div className="flex gap-2">
            <Button onClick={run(onApprove)} disabled={busy} className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700" data-testid={`punch-approve-${p.punch_id}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-5 h-5 mr-1" />}Approve
            </Button>
            <Button onClick={() => onReject(p)} disabled={busy} variant="outline" className="flex-1 h-12 border-red-300 text-red-700 hover:bg-red-50" data-testid={`punch-reject-${p.punch_id}`}>
              <X className="w-5 h-5 mr-1" />Reject
            </Button>
          </div>
        )}
        {p.face_result === 'no_ref' && p.status !== 'rejected' && (
          <Button onClick={() => onUseAsRef(p)} disabled={busy} variant="ghost" size="sm" className="w-full text-amber-700" data-testid={`punch-use-ref-${p.punch_id}`}>
            <UserSquare2 className="w-4 h-4 mr-1" />Use this selfie as the reference photo
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PunchCard;
